
import {IMoveUserSessionKeyOptions, IUserSessionStorageService} from "./user-session-storage.service.interface";
import {NullableString} from "../../../types/nullable-types";
import {ServiceBase} from "../../service-base";
import {Lazy} from "../../../utils/lazy";
import {EmbeddedStorage} from "../embedded-storage";
import {UserSessionStorageKeys} from "./user-session-storage-keys";
import {EnvSessionStorageKeys} from "../environment-session-storage/environment-session-storage-keys";
import {IStorageOptions} from "../storage.interface";
import {IServiceFactory} from "../../service-factory.interface";
import {reaction} from "mobx";

type UserSessionStorage = EmbeddedStorage<UserSessionStorageKeys, EnvSessionStorageKeys>;

export class UserSessionStorageService extends ServiceBase implements IUserSessionStorageService {

    constructor(services: IServiceFactory) {
        super(services);

        this._updateCurrentAuthorizedUserNameHash().then(() => {
            reaction(() => this.services.user.isAuthorized,
                async () => {
                    if (this._currentAuthorizedUserNameHash && this._currentAuthorizedUserNameHash !== this.services.user.profile.userNameHash) {
                        //when logout we remove the user session storage data
                        this.services.envSessionStorage.removeItem(EnvSessionStorageKeys.UserSessionStorage, {
                            discriminator: this._currentAuthorizedUserNameHash
                        });
                    }
                    await this._updateCurrentAuthorizedUserNameHash();
                });
        })
        this.services.user.profile.waitForProfileInitialization().then(() => {
            this._currentAuthorizedUserNameHash = this.services.user.profile.userNameHash;
        });


    }
    private _currentAuthorizedUserNameHash: NullableString = null;

    private async _updateCurrentAuthorizedUserNameHash(): Promise<void> {
        await this.services.user.profile.waitForProfileInitialization();

        if (this.services.user.isAuthorized) {
            this._currentAuthorizedUserNameHash = this.services.user.profile.userNameHash;
        } else {
            this._currentAuthorizedUserNameHash = null;
        }
    }

    private _createStorage(getStorageDiscriminator: () => string): UserSessionStorage {
        return new EmbeddedStorage<UserSessionStorageKeys, EnvSessionStorageKeys>({
            parentStorage: this.services.envSessionStorage,
            parentStorageKey: EnvSessionStorageKeys.UserSessionStorage,
            getStorageDiscriminator: getStorageDiscriminator,
        })
    }

    private readonly _userSessionStorage: Lazy<UserSessionStorage> =
        new Lazy<UserSessionStorage>(() => {
            return this._createStorage(() => this.services.user.profile.userNameHash);
        });

    private get userSessionStorage(): UserSessionStorage {
        return this._userSessionStorage.value;
    }

    setItem(key: UserSessionStorageKeys, value: string, options?: IStorageOptions): void {
        this.userSessionStorage.setItem(key, value, options);
    }
    getItem(key: UserSessionStorageKeys, options?: IStorageOptions): NullableString {
        return this.userSessionStorage.getItem(key, options);
    }
    setJson(key: UserSessionStorageKeys, value: any, options?: IStorageOptions): void {
        this.userSessionStorage.setJson(key, value, options);
    }
    getJson<TValue = any>(key: UserSessionStorageKeys, options?: IStorageOptions): TValue | null {
        return this.userSessionStorage.getJson(key, options);
    }
    removeItem(key: UserSessionStorageKeys, options?: IStorageOptions): void {
        this.userSessionStorage.removeItem(key, options);
    }

    moveKey(options: IMoveUserSessionKeyOptions): void {
        const fromStorage = this._createStorage(() => options.fromUserNameHash);
        const toStorage = this._createStorage(() => options.toUserNameHash);
        const value = fromStorage.getItem(options.key, options.storageOptions);
        if(value) {
            fromStorage.removeItem(options.key, options.storageOptions);
            toStorage.setItem(options.key, value, options.storageOptions);
        }
    }
}
